import React, { useEffect, useRef, useState } from "react";
import { Alert, LinearProgress, Snackbar } from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { PDFDocument } from "pdf-lib";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, getBlob } from "firebase/storage";
import { auth, db, storage } from "../Config/firebase";
import { gerarSequencial } from "../Utils/sequencial";
import { solicitarTextoSistema } from "../Components/PopupSistema/PopupSistema";
import RelatorioCalibracaoPrensaPrint from "../CalibracaoPrensa/RelatorioCalibracaoPrensaPrint";
import RelatorioRupturaPrint from "./RelatorioRupturaPrint";
import "./RelatorioTecnicoConsolidado.css";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";

const TIPO_LAUDO = "Relatório de Rupturas";
const VERSAO_ESTILO_PDF = "ruptura-estilos-isolados-v4";

function calcularResistenciaDaMoldagem(carga, areaCp) {
  if (String(carga ?? "").trim() === "-") return "-";
  if (carga === null || carga === undefined || carga === "") return "";
  const cargaNumerica = Number(String(carga).replace(",", "."));
  const area = Number(areaCp) || 7854;

  if (!Number.isFinite(cargaNumerica) || !cargaNumerica) return "";
  return ((cargaNumerica * 9806) / area).toFixed(1).replace(".", ",");
}

function aguardarRenderizacao() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });
}

function nomeSeguroArquivo(valor) {
  return String(valor || "OBRA")
    .replace(/[<>:"/\\|?*]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function timestampEmMilissegundos(valor) {
  if (!valor) return null;
  if (typeof valor.toMillis === "function") return valor.toMillis();
  if (typeof valor.seconds === "number") {
    return valor.seconds * 1000 + Math.floor((valor.nanoseconds || 0) / 1000000);
  }

  const data = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data.getTime();
}

function formatarTempoGeracao(milissegundos) {
  const totalSegundos = Math.max(0, Math.round(milissegundos / 1000));
  const minutos = Math.floor(totalSegundos / 60);
  const segundos = totalSegundos % 60;

  return minutos > 0
    ? `${minutos} min ${String(segundos).padStart(2, "0")} s`
    : `${segundos} s`;
}

async function calcularAssinaturaBloco(dados) {
  const conteudo = JSON.stringify(dados);
  const bytes = new TextEncoder().encode(conteudo);
  const hash = await window.crypto.subtle.digest("SHA-256", bytes);

  return Array.from(new Uint8Array(hash))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function identificadorSeguroStorage(valor) {
  return String(valor || "bloco")
    .replace(/[^a-zA-Z0-9_-]/g, "-")
    .slice(0, 120);
}

export default function RelatorioTecnicoConsolidado({
  moldagens,
  certificados,
  cliente,
  obra,
  numeroRelatorioInicial,
  tipoGeracaoRelatorio = "novo",
  numeroRevisaoRelatorio = "0",
  pdfRevisaoAnterior,
  listaMoldagens,
  nomeEmpresaSaaS = "Empresa responsável",
  semAlteracaoDesdeUltimoSalvamento = false,
  onVoltar
}) {

  const relatorioRef = useRef(null);
  const pdfCacheRef = useRef({ chave: "", arquivo: null });
  const blocosPdfGeradoRef = useRef([]);
  const [salvandoRelatorio, setSalvandoRelatorio] = useState(false);
  const [imprimindoRelatorio, setImprimindoRelatorio] = useState(false);
  const [relatorioEnviado, setRelatorioEnviado] = useState(false);
  const [relatorioSemAlteracao, setRelatorioSemAlteracao] = useState(
    semAlteracaoDesdeUltimoSalvamento
  );
  const [progressoSalvamento, setProgressoSalvamento] = useState(0);
  const [etapaSalvamento, setEtapaSalvamento] = useState("");
  const [tempoGeracaoRelatorio, setTempoGeracaoRelatorio] = useState(null);
  const [notificacao, setNotificacao] = useState({
    aberta: false,
    mensagem: "",
    tipo: "success"
  });
  const [numeroRelatorio, setNumeroRelatorio] = useState(
    numeroRelatorioInicial || null
  );
  const hoje = new Date().toLocaleDateString("pt-BR");
  const empresa = cliente?.nome || moldagens[0]?.cliente || "-";
  const localObra = obra?.nome || moldagens[0]?.obra || "-";
  const obraId = obra?.id || moldagens[0]?.obraId;
  const revisao = tipoGeracaoRelatorio === "revisao"
    ? String(numeroRevisaoRelatorio)
    : "0";

  useEffect(() => {
    setRelatorioSemAlteracao(semAlteracaoDesdeUltimoSalvamento);
  }, [semAlteracaoDesdeUltimoSalvamento]);

  async function carregarManifestoAnterior() {
    if (!obraId) return { blocos: [], storagePath: null, url: null };

    const snapshot = await getDocs(
      consultaTenantAtual(query(collection(db, "laudos"), where("obraId", "==", obraId)))
    );

    const ultimoLaudoComBlocos = snapshot.docs
      .map((documento) => documento.data())
      .filter(
        (laudo) =>
          laudo.tipo === TIPO_LAUDO &&
          laudo.cancelado !== true &&
          Array.isArray(laudo.blocosRelatorio) &&
          laudo.blocosRelatorio.length > 0
      )
      .sort(
        (a, b) =>
          (timestampEmMilissegundos(b.criadoEm) || 0) -
          (timestampEmMilissegundos(a.criadoEm) || 0)
      )[0];

    return {
      blocos: ultimoLaudoComBlocos?.blocosRelatorio || [],
      storagePath: ultimoLaudoComBlocos?.storagePath || null,
      url: ultimoLaudoComBlocos?.url || null
    };
  }

  async function gerarPdf(onProgresso, persistirBlocos = false) {
  const chavePdf = JSON.stringify({
    versaoEstilo: VERSAO_ESTILO_PDF,
    persistirBlocos,
    numeroRelatorio,
    revisao,
    tipoGeracaoRelatorio,
    pdfRevisaoAnterior: pdfRevisaoAnterior?.url || pdfRevisaoAnterior?.name || "",
    moldagens: moldagens.map((item) => ({
      id: item.id || item.moldagemId || "",
      datamoldagem: item.datamoldagem || "",
      idades: item.idades || [],
      datasRuptura: item.datasRuptura || [],
      areaCp: item.areaCp || "",
      caminhoes: (item.caminhoes || []).map((caminhao) => ({
        id: caminhao.id || "",
        cod_caminhao: caminhao.cod_caminhao || "",
        notaFiscalRomaneio: caminhao.notaFiscalRomaneio || "",
        qtd: caminhao.qtd ?? "",
        slumpObtido: caminhao.slumpObtido ?? "",
        pecaConcretada: caminhao.pecaConcretada || "",
        observacoes: caminhao.observacoes || "",
        resultados: (caminhao.resultados || []).map((resultado) => ({
          cp1: resultado?.cp1 ?? "",
          cp2: resultado?.cp2 ?? ""
        }))
      }))
    })),
    certificados: certificados.map((item) => ({
      id: item.id || "",
      padrao: item.certificadoPadraoArquivo?.id || item.certificadoPadraoArquivo?.url || "",
      externo: item.calibracaoExterna === true,
      arquivoExterno: item.arquivoCertificado?.storagePath || item.arquivoCertificado?.url || ""
    }))
  });

  if (pdfCacheRef.current.chave === chavePdf && pdfCacheRef.current.arquivo) {
    return pdfCacheRef.current.arquivo;
  }

  function armazenarPdfNoCache(arquivo) {
    pdfCacheRef.current = { chave: chavePdf, arquivo };
    return arquivo;
  }

  const paginas = Array.from(
    relatorioRef.current?.querySelectorAll(
      ".relatorio-capa, .relatorio-ruptura-print .pagina, .certificados-capa, .certificado-calibracao-anexo"
    ) || []
  );

  if (!paginas.length) {
    throw new Error("Nenhuma página encontrada para gerar o PDF.");
  }

  const certificadosPadrao = Array.from(new Map(
    certificados
      .map((certificado) => certificado.certificadoPadraoArquivo)
      .filter((certificado) => certificado?.storagePathAtual || certificado?.url)
      .map((certificado) => [certificado.id || certificado.storagePathAtual || certificado.url, certificado])
  ).values());

  const certificadosExternos = Array.from(new Map(
    certificados
      .filter((certificado) => certificado.calibracaoExterna === true)
      .map((certificado) => certificado.arquivoCertificado)
      .filter((arquivo) => arquivo?.storagePath || arquivo?.url)
      .map((arquivo) => [arquivo.storagePath || arquivo.url, arquivo])
  ).values());

  async function adicionarCertificadosPadrao(pdfDestino) {
    for (const certificadoPadrao of certificadosPadrao) {
      let arrayBuffer;
      if (certificadoPadrao.storagePathAtual) {
        const blob = await getBlob(ref(storage, certificadoPadrao.storagePathAtual));
        arrayBuffer = await blob.arrayBuffer();
      } else {
        const resposta = await fetch(certificadoPadrao.url);
        if (!resposta.ok) {
          throw new Error(`Não foi possível carregar o certificado padrão ${certificadoPadrao.numeroCertificado || ""}.`);
        }
        arrayBuffer = await resposta.arrayBuffer();
      }
      const pdfPadrao = await PDFDocument.load(arrayBuffer);
      const paginasPadrao = await pdfDestino.copyPages(pdfPadrao, pdfPadrao.getPageIndices());
      paginasPadrao.forEach((pagina) => pdfDestino.addPage(pagina));
    }
  }

  async function gerarPdfDasPaginas(paginasSelecionadas, progressoInicial = 0, progressoFinal = 100) {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true
    });

    for (let index = 0; index < paginasSelecionadas.length; index += 1) {
      const percentual =
        progressoInicial +
        Math.round((index / paginasSelecionadas.length) * (progressoFinal - progressoInicial));

      onProgresso?.(index, paginasSelecionadas.length, percentual);

      const paginaOriginal = paginasSelecionadas[index];
      paginaOriginal.setAttribute("data-pagina-em-captura", "true");

      let canvas;
      try {
        canvas = await html2canvas(paginaOriginal, {
          backgroundColor: "#ffffff",
          scale: 3,
          useCORS: true,
          logging: false,
          imageTimeout: 15000,
          removeContainer: true,
          onclone: (documentoClonado) => {
            const paginaClonada = documentoClonado.querySelector(
              '.relatorio-ruptura-print .pagina[data-pagina-em-captura="true"]'
            );
            paginaClonada?.classList.add("captura-pdf-ruptura");
          }
        });
      } finally {
        paginaOriginal.removeAttribute("data-pagina-em-captura");
      }

      if (index > 0) pdf.addPage("a4", "portrait");

      pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        0,
        0,
        210,
        297,
        undefined,
        "SLOW"
      );
    }

    return pdf.output("arraybuffer");
  }

  async function gerarPdfDeUmaPagina(paginaOriginal) {
    paginaOriginal.setAttribute("data-pagina-em-captura", "true");

    let canvas;
    try {
      canvas = await html2canvas(paginaOriginal, {
        backgroundColor: "#ffffff",
        scale: 3,
        useCORS: true,
        logging: false,
        imageTimeout: 15000,
        removeContainer: true,
        onclone: (documentoClonado) => {
          const paginaClonada = documentoClonado.querySelector(
            '.relatorio-ruptura-print .pagina[data-pagina-em-captura="true"]'
          );
          paginaClonada?.classList.add("captura-pdf-ruptura");
        }
      });
    } finally {
      paginaOriginal.removeAttribute("data-pagina-em-captura");
    }

    const pdfPagina = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true
    });

    pdfPagina.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      0,
      0,
      210,
      297,
      undefined,
      "SLOW"
    );

    return pdfPagina.output("arraybuffer");
  }

  const pdfFinal = await PDFDocument.create();

  async function adicionarPdfAoFinal(arrayBuffer) {
    const pdfOrigem = await PDFDocument.load(arrayBuffer);
    const paginasCopiadas = await pdfFinal.copyPages(
      pdfOrigem,
      pdfOrigem.getPageIndices()
    );

    paginasCopiadas.forEach((pagina) => {
      pdfFinal.addPage(pagina);
    });
  }

  function obterChavePaginaRuptura(pagina, indiceGlobal) {
    const blocoMoldagem = pagina.closest("[data-bloco-moldagem]");
    const identificadorMoldagem =
      blocoMoldagem?.getAttribute("data-bloco-moldagem") || "moldagem-sem-id";
    const paginasDaMoldagem = blocoMoldagem
      ? Array.from(blocoMoldagem.querySelectorAll(".relatorio-ruptura-print .pagina"))
      : [];
    const indiceNaMoldagem = paginasDaMoldagem.indexOf(pagina);

    return `${identificadorMoldagem}|pagina-${
      indiceNaMoldagem >= 0 ? indiceNaMoldagem : indiceGlobal
    }`;
  }

  async function adicionarPaginasRupturaIncrementais(
    pdfDestino,
    paginasRuptura,
    progressoInicial = 10,
    progressoFinal = 70
  ) {
    if (!paginasRuptura.length) {
      if (persistirBlocos) blocosPdfGeradoRef.current = [];
      return;
    }

    const relatorioAnterior = await carregarManifestoAnterior();
    const manifestoAnterior = relatorioAnterior.blocos;
    const descritores = await Promise.all(
      paginasRuptura.map(async (pagina, indice) => {
        const chave = obterChavePaginaRuptura(pagina, indice);
        const assinatura = await calcularAssinaturaBloco({
          versaoEstilo: VERSAO_ESTILO_PDF,
          chave,
          conteudo: pagina.outerHTML
        });

        return { pagina, chave, assinatura, ordem: indice };
      })
    );

    let primeiraMudancaEstrutural = -1;
    const limiteComparacao = Math.min(descritores.length, manifestoAnterior.length);

    for (let indice = 0; indice < limiteComparacao; indice += 1) {
      if (descritores[indice].chave !== manifestoAnterior[indice]?.chave) {
        primeiraMudancaEstrutural = indice;
        break;
      }
    }

    if (
      primeiraMudancaEstrutural < 0 &&
      descritores.length !== manifestoAnterior.length
    ) {
      primeiraMudancaEstrutural = limiteComparacao;
    }

    const anteriorPorChave = new Map(
      manifestoAnterior.map((bloco) => [bloco.chave, bloco])
    );
    const novoManifesto = [];
    let pdfAnterior = null;

    if (manifestoAnterior.length > 0) {
      try {
        let bytesPdfAnterior;
        if (relatorioAnterior.storagePath) {
          bytesPdfAnterior = await (
            await getBlob(ref(storage, relatorioAnterior.storagePath))
          ).arrayBuffer();
        } else if (relatorioAnterior.url) {
          const resposta = await fetch(relatorioAnterior.url);
          if (!resposta.ok) {
            throw new Error("Não foi possível carregar o último relatório.");
          }
          bytesPdfAnterior = await resposta.arrayBuffer();
        }

        if (bytesPdfAnterior) {
          pdfAnterior = await PDFDocument.load(bytesPdfAnterior);
        }
      } catch (error) {
        console.warn(
          "Não foi possível carregar o relatório anterior; as páginas serão geradas novamente.",
          error
        );
      }
    }

    for (let indice = 0; indice < descritores.length; indice += 1) {
      const descritor = descritores[indice];
      const anterior = anteriorPorChave.get(descritor.chave);
      const percentual = progressoInicial + Math.round(
        (indice / descritores.length) * (progressoFinal - progressoInicial)
      );
      onProgresso?.(indice, descritores.length, percentual);

      const estaDepoisDeInsercao =
        primeiraMudancaEstrutural >= 0 && indice >= primeiraMudancaEstrutural;
      const podeReutilizar =
        !estaDepoisDeInsercao &&
        anterior?.assinatura === descritor.assinatura &&
        Number.isInteger(anterior?.indicePdf) &&
        Boolean(pdfAnterior);

      const indicePdf = pdfDestino.getPageCount();
      let paginaAdicionada = false;

      if (podeReutilizar) {
        try {
          const [paginaCopiada] = await pdfDestino.copyPages(
            pdfAnterior,
            [anterior.indicePdf]
          );
          pdfDestino.addPage(paginaCopiada);
          paginaAdicionada = true;
        } catch (error) {
          console.warn(
            `Não foi possível reutilizar a página ${descritor.chave}; ela será gerada novamente.`,
            error
          );
        }
      }

      if (!paginaAdicionada) {
        const paginaPdf = await gerarPdfDeUmaPagina(descritor.pagina);
        await adicionarPdfAoFinal(paginaPdf);
      }

      novoManifesto.push({
        chave: descritor.chave,
        assinatura: descritor.assinatura,
        ordem: indice,
        indicePdf
      });
    }

    if (persistirBlocos) {
      blocosPdfGeradoRef.current = novoManifesto;
    }
  }

  async function adicionarCertificadosExternos(pdfDestino) {
    for (const arquivo of certificadosExternos) {
      let arrayBuffer;
      if (arquivo.storagePath) {
        arrayBuffer = await (await getBlob(ref(storage, arquivo.storagePath))).arrayBuffer();
      } else {
        const resposta = await fetch(arquivo.url);
        if (!resposta.ok) throw new Error("Não foi possível carregar o certificado externo da prensa.");
        arrayBuffer = await resposta.arrayBuffer();
      }
      const pdfExterno = await PDFDocument.load(arrayBuffer);
      const paginas = await pdfDestino.copyPages(pdfExterno, pdfExterno.getPageIndices());
      paginas.forEach((pagina) => pdfDestino.addPage(pagina));
    }
  }

  let pdfBase = null;

  if (pdfRevisaoAnterior instanceof File) {
    pdfBase = await pdfRevisaoAnterior.arrayBuffer();
  } else if (pdfRevisaoAnterior?.storagePath) {
    const blobBase = await getBlob(ref(storage, pdfRevisaoAnterior.storagePath));
    pdfBase = await blobBase.arrayBuffer();
  } else if (pdfRevisaoAnterior?.url) {
    const resposta = await fetch(pdfRevisaoAnterior.url);
    if (!resposta.ok) {
      throw new Error("Não foi possível carregar o PDF-base do relatório.");
    }
    pdfBase = await resposta.arrayBuffer();
  }

  const paginasCapa = paginas.filter((pagina) =>
    pagina.classList.contains("relatorio-capa")
  );
  const paginasRuptura = paginas.filter((pagina) =>
    pagina.classList.contains("pagina") &&
    Boolean(pagina.closest(".relatorio-ruptura-print"))
  );
  const paginasAuxiliares = paginas.filter((pagina) =>
    !pagina.classList.contains("relatorio-capa") &&
    !paginasRuptura.includes(pagina)
  );

  if (paginasCapa.length) {
    const pdfCapa = await gerarPdfDasPaginas(paginasCapa, 0, 10);
    await adicionarPdfAoFinal(pdfCapa);
  }

  if (pdfBase) {
    await adicionarPdfAoFinal(pdfBase);
  }

  await adicionarPaginasRupturaIncrementais(
    pdfFinal,
    paginasRuptura,
    10,
    65
  );

  if (paginasAuxiliares.length) {
    const pdfAuxiliar = await gerarPdfDasPaginas(paginasAuxiliares, 65, 70);
    await adicionarPdfAoFinal(pdfAuxiliar);
  }

  await adicionarCertificadosPadrao(pdfFinal);
  await adicionarCertificadosExternos(pdfFinal);

  const bytesFinal = await pdfFinal.save();

  return armazenarPdfNoCache(new Blob([bytesFinal], { type: "application/pdf" }));
}

  async function imprimirRelatorio() {
    if (imprimindoRelatorio || salvandoRelatorio) return;

    setImprimindoRelatorio(true);
    try {
      const pdf = await gerarPdf();
      const url = URL.createObjectURL(pdf);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = url;

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          URL.revokeObjectURL(url);
          iframe.remove();
          setImprimindoRelatorio(false);
        }, 3000);
      };

      document.body.appendChild(iframe);
    } catch (error) {
      console.error("Erro ao imprimir relatório:", error);
      setImprimindoRelatorio(false);
      setNotificacao({
        aberta: true,
        mensagem: error.message || "Não foi possível preparar o relatório para impressão.",
        tipo: "error"
      });
    }
  }

  async function obterProximoNumero(solicitarPrimeiro = true) {
    if (tipoGeracaoRelatorio === "revisao" && numeroRelatorioInicial) {
      return numeroRelatorioInicial;
    }

    const snapshot = await getDocs(
      consultaTenantAtual(query(collection(db, "laudos"), where("obraId", "==", obraId)))
    );
    const relatoriosDaObra = snapshot.docs
      .map((documento) => documento.data())
      .filter((laudo) => laudo.tipo === TIPO_LAUDO);
    const numeros = relatoriosDaObra
      .map((laudo) => {
        const numeroDireto = Number(laudo.numeroRelatorio);
        if (Number.isInteger(numeroDireto) && numeroDireto > 0) return numeroDireto;

        const numeroNoTexto = String(
          laudo.descricao || laudo.nomeArquivo || ""
        ).match(/(?:rel[.\s_-]*|n[º°o.]?\s*|rupturas-)(\d+)/i);
        return numeroNoTexto ? Number(numeroNoTexto[1]) : null;
      })
      .filter((numero) => Number.isInteger(numero) && numero > 0);

    const maiorNumero = numeros.length > 0 ? Math.max(...numeros) : 0;

    if (!solicitarPrimeiro) {
      if (maiorNumero > 0) return maiorNumero + 1;
      if (numeroRelatorioInicial) return numeroRelatorioInicial;
      return null;
    }

    if (maiorNumero > 0 || numeroRelatorioInicial) {
      const valorMinimo = maiorNumero > 0
        ? maiorNumero
        : Number(numeroRelatorioInicial || 1) - 1;
      const sequencial = await gerarSequencial({
        chave: "relatorio_tecnico_consolidado",
        escopo: obraId,
        valorMinimo,
      });
      return sequencial.numero;
    }


    const informado = await solicitarTextoSistema("Digite o número do primeiro relatório desta obra:", "1");
    if (informado === null) return null;

    const numero = Number(informado);
    if (!Number.isInteger(numero) || numero <= 0) {
      throw new Error("Informe um número de relatório válido.");
    }

    const sequencial = await gerarSequencial({
      chave: "relatorio_tecnico_consolidado",
      escopo: obraId,
      valorMinimo: numero - 1,
    });
    return sequencial.numero;
  }

  useEffect(() => {
    if (!obraId) return;

    let ativo = true;

    obterProximoNumero(false)
      .then((numero) => {
        if (ativo) setNumeroRelatorio(numero);
      })
      .catch((error) => {
        console.error("Erro ao consultar número do relatório:", error);
      });

    return () => {
      ativo = false;
    };
  }, [obraId, numeroRelatorioInicial]);

  async function salvarRelatorio() {
    if (salvandoRelatorio || imprimindoRelatorio || relatorioEnviado || relatorioSemAlteracao) return;

    if (!obraId) {
      setNotificacao({
        aberta: true,
        mensagem: "Não foi possível identificar a obra deste relatório.",
        tipo: "error"
      });
      return;
    }

    if (!auth.currentUser) {
      setNotificacao({
        aberta: true,
        mensagem: "Usuário não autenticado.",
        tipo: "error"
      });
      return;
    }

    const inicioGeracao = performance.now();
    setTempoGeracaoRelatorio(null);
    setSalvandoRelatorio(true);
    setProgressoSalvamento(5);
    setEtapaSalvamento("Consultando numeração do relatório...");

    try {
      const obraRef = doc(db, "obras", obraId);
      const obraSnap = await getDoc(obraRef);

      if (obraSnap.exists()) {
        const dadosObra = obraSnap.data();
        const ultimaAlteracao = timestampEmMilissegundos(
          dadosObra.ultimaalteracaoagendaruptura
        );
        const ultimoRelatorio = timestampEmMilissegundos(
          dadosObra.ultimorelatsalvo
        );

        if (
          ultimaAlteracao !== null &&
          ultimoRelatorio !== null &&
          ultimaAlteracao <= ultimoRelatorio
        ) {
          setProgressoSalvamento(0);
          setEtapaSalvamento("");
          setRelatorioSemAlteracao(true);
          return;
        }
      }

      const proximoNumero = await obterProximoNumero();
      if (proximoNumero === null) return;

      setProgressoSalvamento(10);
      setEtapaSalvamento("Preparando as páginas...");
      setNumeroRelatorio(proximoNumero);
      await aguardarRenderizacao();

      const numeroFormatado = String(proximoNumero).padStart(3, "0");
      const dataArquivo = hoje.replace(/\//g, "-");
      const identificacaoRevisao =
        tipoGeracaoRelatorio === "revisao" ? `.REV.${revisao}` : "";
      const descricao = `REL.${numeroFormatado}${identificacaoRevisao}.${nomeSeguroArquivo(localObra)} - ${dataArquivo}`;
      const nomeArquivo = `${descricao}.pdf`;
      const storagePath = `laudos/${obraId}/${nomeArquivo}`;
      const pdf = await gerarPdf((paginaAtual, totalPaginas) => {
        const percentual = 10 + Math.round((paginaAtual / totalPaginas) * 60);
        setProgressoSalvamento(percentual);
        setEtapaSalvamento(
          `Gerando PDF: página ${Math.min(paginaAtual + 1, totalPaginas)} de ${totalPaginas}...`
        );
      }, true);

      const tamanhoPdfMb = pdf.size / (1024 * 1024);
      if (tamanhoPdfMb >= 100) {
        throw new Error(
          `O relatório ficou com ${tamanhoPdfMb.toFixed(1)} MB e ultrapassou o limite de 100 MB. Atualize o PDF-base ou reduza a quantidade de páginas.`
        );
      }

      setProgressoSalvamento(75);
      setEtapaSalvamento("Enviando o relatório...");
      await uploadBytes(ref(storage, storagePath), pdf, {
        contentType: "application/pdf"
      });

      const urlPdfFinal = await getDownloadURL(ref(storage, storagePath));

      setProgressoSalvamento(90);
      setEtapaSalvamento("Registrando nos laudos...");
    await addDoc(collection(db, "laudos"), aplicarTenantAtual({
  obraId,
  tipo: TIPO_LAUDO,
  descricao,
  numeroRelatorio: proximoNumero,
  numeroRevisao: revisao,
  nomeArquivo,
  storagePath,
  url: urlPdfFinal,
  blocosRelatorio: blocosPdfGeradoRef.current,
  relatorioAnteriorUsadoNaRevisao: pdfRevisaoAnterior
  ? {
      nomeArquivo:
        pdfRevisaoAnterior.nomeArquivo ||
        pdfRevisaoAnterior.name ||
        "relatorio-antigo.pdf",
      storagePath: pdfRevisaoAnterior.storagePath || null,
      url: pdfRevisaoAnterior.url || null,
      tamanho: pdfRevisaoAnterior.size || null
    }
  : null,
  cancelado: false,
  criadoPor: auth.currentUser.uid,
  criadoEm: serverTimestamp()
}));

      await updateDoc(obraRef, {
        ultimaalteracaoagendaruptura: serverTimestamp(),
        ultimorelatsalvo: serverTimestamp()
      });

      setProgressoSalvamento(100);
      setEtapaSalvamento("Relatório salvo com sucesso.");
      const tempoTotal = performance.now() - inicioGeracao;
      setTempoGeracaoRelatorio(tempoTotal);
      setRelatorioEnviado(true);
    } catch (error) {
      console.error("Erro ao salvar relatório:", error);
      setNotificacao({
        aberta: true,
        mensagem: error.message || "Não foi possível salvar o relatório.",
        tipo: "error"
      });
    } finally {
      setSalvandoRelatorio(false);
    }
  }

  return (
    <div className="relatorio-tecnico-consolidado" ref={relatorioRef}>
      <div className="relatorio-consolidado-acoes">
        <div className="relatorio-consolidado-botoes">
          <button
            type="button"
            className="acao-voltar"
            onClick={onVoltar}
            disabled={salvandoRelatorio}
          >
            Voltar
          </button>
          <button
            type="button"
            className="acao-imprimir"
            onClick={imprimirRelatorio}
            disabled={salvandoRelatorio || imprimindoRelatorio}
          >
            {imprimindoRelatorio ? "Preparando..." : "Imprimir"}
          </button>
          {relatorioSemAlteracao ? (
            <span className="relatorio-sem-alteracao">Sem alteração</span>
          ) : (
          <button
            type="button"
            className="acao-salvar"
            onClick={salvarRelatorio}
            disabled={salvandoRelatorio || imprimindoRelatorio || relatorioEnviado}
          >
            {salvandoRelatorio
              ? "Salvando..."
              : relatorioEnviado
                ? "Relatório salvo"
                : "Salvar relatório"}
          </button>
          )}
        </div>

        {salvandoRelatorio && (
          <div className="relatorio-salvamento-progresso">
            <div>
              <span>{etapaSalvamento}</span>
              <strong>{progressoSalvamento}%</strong>
            </div>
            <LinearProgress
              variant="determinate"
              value={progressoSalvamento}
              aria-label="Progresso do salvamento do relatório"
            />
          </div>
        )}

        {relatorioEnviado && tempoGeracaoRelatorio !== null && (
          <div className="relatorio-tempo-conclusao">
            Relatório concluído em{" "}
            <strong>{formatarTempoGeracao(tempoGeracaoRelatorio)}</strong>
          </div>
        )}
      </div>

      <Snackbar
        open={notificacao.aberta && notificacao.tipo !== "success"}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
      >
        <Alert
          severity={notificacao.tipo}
          variant="filled"
          onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
          sx={{ width: "100%" }}
        >
          {notificacao.mensagem}
        </Alert>
      </Snackbar>

      <section className="relatorio-capa">
        <div className="relatorio-capa-moldura">
          <LogoRelatorio alt="Logo da empresa"  />

          <div className="relatorio-capa-titulo">
            <strong>
              {numeroRelatorio
                ? `R${String(numeroRelatorio).padStart(2, "0")}${
                    tipoGeracaoRelatorio === "revisao" ? ` REV.${revisao}` : ""
                  }`
                : "R--"}
            </strong>
            <span>RELATÓRIO TÉCNICO - CONCRETO</span>
          </div>

          <dl className="relatorio-capa-dados">
            <div>
              <dt>CLIENTE:</dt>
              <dd>{cliente?.nome || moldagens[0]?.cliente || "-"}</dd>
            </div>
            <div>
              <dt>EMPREENDIMENTO:</dt>
              <dd>{obra?.nome || moldagens[0]?.obra || "-"}</dd>
            </div>
            <div>
              <dt>ENDEREÇO:</dt>
              <dd>{obra?.endereco || "-"}</dd>
            </div>
          </dl>

          <div className="relatorio-capa-texto">
            <strong>OBSERVAÇÃO:</strong>
            <p>
              O CONTROLE TECNOLÓGICO ESTÁ SENDO REALIZADO CONFORME A ABNT NBR
              12655:2022, CONCRETO DE CIMENTO PORTLAND - PREPARO, CONTROLE,
              RECEBIMENTO E ACEITAÇÃO-PROCEDIMENTO - ITEM 6.2.3.1 CONTROLE DE
              CONCRETO POR AMOSTRAGEM TOTAL (100%)
            </p>
            <p>ONDE</p>
            <p>
              f<sub>ck,betonada</sub> É O VALOR DA RESISTÊNCIA À COMPRESSÃO DO
              EXEMPLAR QUE APRESENTA O CONCRETO DA BETONADA, OU SEJA, NÃO HÁ
              INFERÊNCIA ESTATÍSTICA.
            </p>
            <p>RELATÓRIOS DIVIDIDOS POR DATA DE MOLDAGEM E/OU TIPO DE CONCRETO.</p>
            <p>
              <strong>Anexo:</strong> Certificado de calibração - Prensa de Concreto
            </p>
          </div>

          <div className="relatorio-capa-rodape">
            <strong>Palmas,TO</strong>
            <span>{hoje}</span>
          </div>
        </div>
      </section>

      {moldagens.map((moldagem) => (
        <div
          key={moldagem.id}
          data-bloco-moldagem={`moldagem-${moldagem.id || moldagem.moldagemId}`}
        >
          <RelatorioRupturaPrint
            form={moldagem}
            ensaios={moldagem.caminhoes || []}
            calcularResistencia={(carga) =>
              calcularResistenciaDaMoldagem(carga, moldagem.areaCp)
            }
            obraId={moldagem.obraId}
            obraDados={obra}
            listaMoldagens={listaMoldagens}
            moldagemId={moldagem.id}
            nomeEmpresaSaaS={nomeEmpresaSaaS}
            autoPrint={false}
          />
        </div>
      ))}

      {certificados.length > 0 && (
        <section className="certificados-anexos">
          <div className="certificados-capa">
            <table className="certificados-cabecalho">
              <tbody>
                <tr>
                  <td rowSpan="3" className="certificados-logo">
                    <LogoRelatorio alt="Logo da empresa"  />
                  </td>
                  <td rowSpan="3" className="certificados-titulo">
                    SISTEMA DE GEST&Atilde;O DA QUALIDADE
                    <br />
                    Relat&oacute;rio - ENSAIO DE COMPRESS&Atilde;O DE CORPOS DE PROVA
                    CIL&Iacute;NDRICOS - ABNT NBR 5739
                  </td>
                  <td className="certificados-revisao">RQ 2.2.11</td>
                </tr>
                <tr><td className="certificados-revisao">REVIS&Atilde;O 00</td></tr>
                <tr><td className="certificados-revisao">20/08/2025</td></tr>
                <tr>
                  <td colSpan="3" className="certificados-data">
                    <strong>DATA:</strong> {hoje}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="certificados-identificacao">
              <div className="certificados-empresa"><strong>EMPRESA:</strong><span>{empresa}</span></div>
              <div className="certificados-obra"><strong>LOCAL/OBRA:</strong><span>{localObra}</span></div>
            </div>

            <div className="certificados-faixa">
              ANEXO - Certificado de calibra&ccedil;&atilde;o - Prensa de Concreto
            </div>

            <div className="certificados-assinatura">
              <img src="/Images/assinaturaLeal.png" alt="Assinatura" />
              <strong>Jose Carlos Lemes Leal</strong>
              <span>Eng. Civil CREA 203379/D - TO</span>
            </div>

            <div className="certificados-rodape">
              DOCUMENTO PARA USO RESTRITO. REPRODU&Ccedil;&Atilde;O/C&Oacute;PIA PROIBIDA SEM
              AUTORIZA&Ccedil;&Atilde;O DA EMPRESA
            </div>
            <p>Certificados de calibração das prensas utilizadas</p>
          </div>

          {certificados.filter((certificado) => certificado.calibracaoExterna !== true).map((certificado) => (
            <div
              className="certificado-calibracao-anexo"
              key={certificado.id}
            >
              <RelatorioCalibracaoPrensaPrint dados={certificado} />
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
